/**
 * State and handlers for WorkflowTools config management and provider connect flow.
 */
import { useState, useCallback, type FormEvent } from "react";
import { useWorkflowConfigs, useUpdateConfig, useDeleteConfig } from "./useWorkflowQueries";
import type { ConfigEntry } from "./types";
import { type ProviderDefinition } from "./workflowToolsProviders";

const KNOWN_NAMESPACES = ["databases", "apis", "slack", "agent"];

export const useWorkflowToolsState = () => {
  const { data, isLoading, error } = useWorkflowConfigs();
  const upsertMutation = useUpdateConfig();
  const deleteMutation = useDeleteConfig();

  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ConfigEntry | null>(null);
  const [formNs, setFormNs] = useState("databases");
  const [formCustomNs, setFormCustomNs] = useState("");
  const [formKey, setFormKey] = useState("");
  const [formValue, setFormValue] = useState("");
  const [formEncrypted, setFormEncrypted] = useState(false);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [activeProvider, setActiveProvider] = useState<ProviderDefinition | null>(null);
  const [providerValues, setProviderValues] = useState<Record<string, string>>({});
  const [providerSaving, setProviderSaving] = useState(false);

  const configs = data?.configs ?? [];
  const grouped = configs.reduce<Record<string, ConfigEntry[]>>((acc, c) => {
    if (!acc[c.namespace]) acc[c.namespace] = [];
    acc[c.namespace].push(c);
    return acc;
  }, {});
  const allNamespaces = Array.from(new Set([...KNOWN_NAMESPACES, ...Object.keys(grouped)])).sort();

  const getProviderConfigCount = (provider: ProviderDefinition): number => {
    const nsEntries = grouped[provider.namespace] ?? [];
    return provider.fields.filter(f => nsEntries.some(e => e.key === `${provider.id}_${f.key}`)).length;
  };

  const resetForm = useCallback(() => {
    setFormNs("databases"); setFormCustomNs(""); setFormKey("");
    setFormValue(""); setFormEncrypted(false); setEditingEntry(null); setShowForm(false);
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const namespace = formNs === "__custom__" ? formCustomNs.trim() : formNs;
    if (!namespace || !formKey.trim()) return;
    let parsedValue: unknown = formValue;
    try { parsedValue = JSON.parse(formValue); } catch { /* keep as string */ }
    upsertMutation.mutate({ namespace, key: formKey.trim(), value: parsedValue, encrypted: formEncrypted }, { onSuccess: resetForm });
  };

  const handleEdit = (entry: ConfigEntry) => {
    setActiveProvider(null); setEditingEntry(entry);
    setFormNs(KNOWN_NAMESPACES.includes(entry.namespace) ? entry.namespace : "__custom__");
    setFormCustomNs(KNOWN_NAMESPACES.includes(entry.namespace) ? "" : entry.namespace);
    setFormKey(entry.key);
    setFormValue(entry.encrypted ? "" : typeof entry.value === "string" ? entry.value : JSON.stringify(entry.value));
    setFormEncrypted(entry.encrypted); setShowForm(true);
  };

  const handleDelete = (entry: ConfigEntry) => deleteMutation.mutate(entry.id);

  const toggleReveal = (id: string) => {
    setRevealedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const openProviderForm = (provider: ProviderDefinition) => {
    setShowForm(false); setActiveProvider(provider);
    const nsEntries = grouped[provider.namespace] ?? [];
    const prefilled: Record<string, string> = {};
    for (const field of provider.fields) {
      const existing = nsEntries.find(e => e.key === `${provider.id}_${field.key}`);
      prefilled[field.key] = (existing && !existing.encrypted)
        ? (typeof existing.value === "string" ? existing.value : JSON.stringify(existing.value))
        : (field.defaultValue ?? "");
    }
    setProviderValues(prefilled);
  };

  const closeProviderForm = () => { setActiveProvider(null); setProviderValues({}); };

  const handleProviderSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!activeProvider) return;
    setProviderSaving(true);
    try {
      await Promise.all(activeProvider.fields
        .filter(field => providerValues[field.key]?.trim())
        .map(field => upsertMutation.mutateAsync({
          namespace: activeProvider.namespace,
          key: `${activeProvider.id}_${field.key}`,
          value: providerValues[field.key].trim(),
          encrypted: field.type === "password",
        })));
      closeProviderForm();
    } finally { setProviderSaving(false); }
  };

  return {
    isLoading, error, configs, grouped, allNamespaces, getProviderConfigCount,
    showForm, setShowForm, editingEntry, formNs, formCustomNs, formKey, formValue,
    formEncrypted, revealedIds, activeProvider, providerValues, providerSaving,
    setFormNs, setFormCustomNs, setFormKey, setFormValue, setFormEncrypted,
    setActiveProvider, setProviderValues, resetForm, handleSubmit, handleEdit,
    handleDelete, toggleReveal, openProviderForm, closeProviderForm, handleProviderSubmit,
    upsertMutation, deleteMutation,
  };
};
